'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type {
  PointerEvent as ReactPointerEvent,
  TouchEvent as ReactTouchEvent,
} from 'react'
import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import ShortVideoSlide from '@/components/shorts/ShortVideoSlide'
import ShortYoutubePlayer from '@/components/shorts/ShortYoutubePlayer'
import { resolveShortsYoutubeId } from '@/components/shorts/ShortVideoPlayer'
import { getApiUrl } from '@/lib/config'
import type { ShortsAudioControl } from '@/lib/shorts-audio-control'
import type { ShortFeedItem, ShortsFeedResponse } from '@/lib/news-types'
import { loadShortsAudioPrefs, saveShortsAudioPrefs, DEFAULT_SHORTS_AUDIO } from '@/lib/shorts-audio-prefs'
import {
  loadShortsAudioUnlocked,
  primeAllShortsAudio,
  saveShortsAudioUnlocked,
} from '@/lib/shorts-audio-session'
import { syncShortsDocumentMeta } from '@/lib/shorts-document-meta'
import { getShortFeedKey, getShortsHrefForItem } from '@/lib/shorts-slug'
import styles from '@/components/shorts/shorts.module.css'

interface ShortsFeedProps {
  initialData: ShortsFeedResponse
  startKey?: string
}

const LAST_BROWSE_KEY = 'haber-shorts-last-browse-key'
/** Fraction of slide height — past this, release commits neighbor. */
const SWIPE_COMMIT_RATIO = 0.12
/** Minimum drag (px) before a short flick can change page. */
const SWIPE_MIN_DRAG = 18
/** Pointer velocity (px/ms) — light flicks commit without a long drag. */
const SWIPE_FLICK_VELOCITY = 0.35
/** Ignore further wheel/keyboard steps until inertia dies. */
const NAV_LOCK_MS = 850
/** Match mobile Shorts page slide duration. */
const SWIPE_ANIM_MS = 200
/** Keep previous + next 2 shorts ready (Shorts-style). */
const SHORTS_READY_BEHIND = 1
const SHORTS_READY_AHEAD = 2

function shouldPreloadShort(index: number, activeIndex: number): boolean {
  return (
    index !== activeIndex &&
    index >= activeIndex - SHORTS_READY_BEHIND &&
    index <= activeIndex + SHORTS_READY_AHEAD
  )
}

function shouldWarmShort(index: number, activeIndex: number): boolean {
  // Keep next two shorts muted-playing so swipe never hits a cold poster.
  return index === activeIndex + 1 || index === activeIndex + 2
}

function warmDelayForIndex(index: number, activeIndex: number): number {
  if (index === activeIndex + 1) return 0
  if (index === activeIndex + 2) return 450
  return 0
}

function collectYoutubeReadyWindow(
  items: ShortFeedItem[],
  activeIndex: number,
  ready: boolean,
): Array<{ id: string; index: number }> {
  if (!ready || activeIndex < 0) return []
  const windowItems: Array<{ id: string; index: number }> = []
  const start = Math.max(0, activeIndex - SHORTS_READY_BEHIND)
  const end = Math.min(items.length - 1, activeIndex + SHORTS_READY_AHEAD)
  for (let index = start; index <= end; index += 1) {
    const id = resolveShortsYoutubeId(items[index]?.youtube)
    if (!id) continue
    windowItems.push({ id, index })
  }
  return windowItems
}

interface SwipeDragState {
  pointerId: number
  startX: number
  startY: number
  startIndex: number
  moved: boolean
  /** Capture only after the pointer actually drags — keeps tap-to-pause working. */
  capturing: boolean
  lastY: number
  lastAt: number
  velocityY: number
}

interface TouchSwipeState {
  identifier: number
  startX: number
  startY: number
  startIndex: number
  moved: boolean
  capturing: boolean
  lastY: number
  lastAt: number
  velocityY: number
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0
  return Math.min(length - 1, Math.max(0, index))
}

/** One gesture may move at most one short away from where it started. */
function clampToSingleStep(
  startIndex: number,
  targetIndex: number,
  length: number,
): number {
  if (targetIndex > startIndex) return clampIndex(startIndex + 1, length)
  if (targetIndex < startIndex) return clampIndex(startIndex - 1, length)
  return clampIndex(startIndex, length)
}

function resolveSettleIndex(
  startIndex: number,
  shift: number,
  slideHeight: number,
  length: number,
  velocityY = 0,
): number {
  if (slideHeight <= 0 || length <= 0) return 0
  const ratio = shift / slideHeight
  const draggedEnough = Math.abs(shift) >= SWIPE_MIN_DRAG
  let target = startIndex
  // Short drag (~12%) or a light flick commits the next short.
  if (
    (ratio >= SWIPE_COMMIT_RATIO && draggedEnough) ||
    (draggedEnough && velocityY > SWIPE_FLICK_VELOCITY)
  ) {
    target = startIndex + 1
  } else if (
    (ratio <= -SWIPE_COMMIT_RATIO && draggedEnough) ||
    (draggedEnough && velocityY < -SWIPE_FLICK_VELOCITY)
  ) {
    target = startIndex - 1
  }
  return clampToSingleStep(startIndex, target, length)
}

function isShortsControlTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(
    target.closest(
      '[data-shorts-control], .plyr__controls, .plyr__control--overlaid, button, a, input, textarea, select, [role="dialog"]',
    ),
  )
}

function chooseBrowseIndex(items: ShortFeedItem[]): number {
  if (items.length <= 1) return 0

  let lastKey = ''
  try {
    lastKey = window.sessionStorage.getItem(LAST_BROWSE_KEY) || ''
  } catch {
    /* Safari gizli modunda storage kullanılamayabilir */
  }

  const candidates = items
    .map((item, index) => ({ index, key: getShortFeedKey(item) }))
    .filter(({ index, key }) => key !== lastKey && (lastKey ? true : index !== 0))
  const pool = candidates.length ? candidates : items.map((item, index) => ({ index, key: getShortFeedKey(item) }))
  const selected = pool[Math.floor(Math.random() * pool.length)]!

  try {
    window.sessionStorage.setItem(LAST_BROWSE_KEY, selected.key)
  } catch {
    /* Rastgele başlangıç storage olmadan da çalışır */
  }
  return selected.index
}

function initialShortsIndex(items: ShortFeedItem[], startKey?: string): number {
  if (!items.length || !startKey) return 0
  const idx = items.findIndex((item) => getShortFeedKey(item) === startKey)
  return idx >= 0 ? idx : 0
}

export default function ShortsFeed({ initialData, startKey }: ShortsFeedProps) {
  const initialIndex = initialShortsIndex(initialData.news || [], startKey)
  const [items, setItems] = useState<ShortFeedItem[]>(initialData.news || [])
  const [page, setPage] = useState(initialData.page || 1)
  const [hasMore, setHasMore] = useState(initialData.hasMore ?? false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const [browseInitialized, setBrowseInitialized] = useState(Boolean(startKey))
  const [audio, setAudio] = useState(DEFAULT_SHORTS_AUDIO)
  const [audioUnlocked, setAudioUnlocked] = useState(false)

  useEffect(() => {
    setAudio(loadShortsAudioPrefs())
    setAudioUnlocked(loadShortsAudioUnlocked())
  }, [])
  const containerRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)
  const didBootRef = useRef(false)
  const activeIndexRef = useRef(activeIndex)
  const urlUpdateRef = useRef<number | undefined>(undefined)
  const swipeDragRef = useRef<SwipeDragState | null>(null)
  const touchSwipeRef = useRef<TouchSwipeState | null>(null)
  const suppressDragClickRef = useRef(false)
  const wheelLockRef = useRef(false)
  const navLockRef = useRef(false)
  const navLockTimerRef = useRef<number | undefined>(undefined)
  const swipeAnimTimerRef = useRef<number | undefined>(undefined)
  const gestureStartIndexRef = useRef(activeIndex)
  const swipeAnimatingRef = useRef(false)
  const sharedAudioControlRef = useRef<ShortsAudioControl | null>(null)
  const feedProgressRef = useRef<HTMLInputElement>(null)
  const feedRemainingRef = useRef<HTMLSpanElement>(null)
  const feedSeekingRef = useRef(false)
  /** After short change, ignore mid-video ticks until promote rewind lands. */
  const progressSyncRef = useRef({ epoch: 0, armedAt: 0, locked: false })
  const [sharedPlaying, setSharedPlaying] = useState(true)
  const [slideHeight, setSlideHeight] = useState(0)
  const [swipeShift, setSwipeShift] = useState(0)
  const [swipeAnchor, setSwipeAnchor] = useState(initialIndex)
  const [swipeVisual, setSwipeVisual] = useState(false)
  const [swipeTransition, setSwipeTransition] = useState(false)

  const feedReady = items.length > 0 && (Boolean(startKey) || browseInitialized)
  const activeItem = feedReady ? items[activeIndex] : undefined
  const sharedYoutubeId = activeItem
    ? resolveShortsYoutubeId(activeItem.youtube)
    : null
  const youtubeHostActive = Boolean(sharedYoutubeId)
  const hostWindowIndex = swipeVisual ? swipeAnchor : activeIndex
  const youtubeReadyWindow = collectYoutubeReadyWindow(
    items,
    hostWindowIndex,
    feedReady,
  )
  const layoutAnchor = swipeVisual ? swipeAnchor : activeIndex
  const trackOffset = -(layoutAnchor * slideHeight + swipeShift)

  const formatRemaining = useCallback((current: number, duration: number) => {
    const left = Math.max(0, Math.ceil((duration || 0) - (current || 0)))
    const minutes = Math.floor(left / 60)
    const seconds = left % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
  }, [])

  const resetFeedProgressUi = useCallback(() => {
    const input = feedProgressRef.current
    if (input) {
      input.value = '0'
      input.max = '100'
      input.style.setProperty('--shorts-progress', '0%')
    }
    if (feedRemainingRef.current) {
      feedRemainingRef.current.textContent = '0:00'
    }
  }, [])

  useEffect(() => {
    if (!sharedYoutubeId) return
    setSharedPlaying(true)
    feedSeekingRef.current = false
    // New short: clear stale bar; first active tick becomes the source of truth
    // (warm peers continue in place — no forced seek-to-0 jump).
    progressSyncRef.current = {
      epoch: progressSyncRef.current.epoch + 1,
      armedAt: Date.now(),
      locked: true,
    }
    resetFeedProgressUi()
  }, [activeIndex, resetFeedProgressUi, sharedYoutubeId])

  const updateFeedProgress = useCallback((current: number, duration: number) => {
    const input = feedProgressRef.current
    if (!input || feedSeekingRef.current) return
    const safeDuration = Math.max(0, duration || 0)
    const safeCurrent = Math.min(
      safeDuration || Number.MAX_SAFE_INTEGER,
      Math.max(0, current || 0),
    )

    // Drop one stale tick from the previous host if callbacks lag a frame.
    const sync = progressSyncRef.current
    if (sync.locked) {
      const waited = Date.now() - sync.armedAt
      if (safeCurrent > 8 && waited < 200) {
        return
      }
      sync.locked = false
    }

    input.max = String(safeDuration || 100)
    input.value = String(safeCurrent)
    const progress =
      safeDuration > 0
        ? Math.min(100, Math.max(0, (safeCurrent / safeDuration) * 100))
        : 0
    input.style.setProperty('--shorts-progress', `${progress}%`)
    if (feedRemainingRef.current) {
      feedRemainingRef.current.textContent = formatRemaining(
        safeCurrent,
        safeDuration,
      )
    }
  }, [formatRemaining])

  const seekActiveShort = useCallback((seconds: number) => {
    sharedAudioControlRef.current?.seekTo(seconds)
  }, [])

  useEffect(() => {
    saveShortsAudioPrefs(audio)
  }, [audio])

  useEffect(() => {
    saveShortsAudioUnlocked(audioUnlocked)
  }, [audioUnlocked])

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const reprimeUnlockedAudio = useCallback(() => {
    if (!audioUnlocked || audio.muted) return
    primeAllShortsAudio(false, audio.volume)
  }, [audio.muted, audio.volume, audioUnlocked])

  const unlockNavigation = useCallback(() => {
    navLockRef.current = false
    wheelLockRef.current = false
    if (navLockTimerRef.current) {
      window.clearTimeout(navLockTimerRef.current)
      navLockTimerRef.current = undefined
    }
  }, [])

  const lockNavigation = useCallback(
    (ms = NAV_LOCK_MS) => {
      navLockRef.current = true
      wheelLockRef.current = true
      if (navLockTimerRef.current) window.clearTimeout(navLockTimerRef.current)
      navLockTimerRef.current = window.setTimeout(() => {
        unlockNavigation()
      }, ms)
    },
    [unlockNavigation],
  )

  const clampSwipeShift = useCallback(
    (anchor: number, shift: number, height: number) => {
      if (height <= 0) return 0
      const minShift = anchor > 0 ? -height : 0
      const maxShift = anchor < items.length - 1 ? height : 0
      return Math.min(maxShift, Math.max(minShift, shift))
    },
    [items.length],
  )

  const finishSwipeSession = useCallback((index: number) => {
    // Snap layout + active index together. Destination transform already equals
    // the settled math — splitting these across frames caused a visible jump.
    setSwipeTransition(false)
    setSwipeShift(0)
    setSwipeAnchor(index)
    setSwipeVisual(false)
    gestureStartIndexRef.current = index
    activeIndexRef.current = index
    setActiveIndex(index)
    swipeAnimatingRef.current = false
    // Keep navLock until NAV_LOCK_MS — trackpad inertia must not chain steps.
  }, [])

  const settleSwipeToIndex = useCallback(
    (targetIndex: number, opts?: { resume?: boolean }) => {
      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (height <= 0) return
      // Block duplicate settles (pointerup + lostcapture, double wheel, etc.).
      if (swipeAnimatingRef.current && !opts?.resume) return

      const start = gestureStartIndexRef.current
      const clamped = clampToSingleStep(start, targetIndex, items.length)
      const targetShift = (clamped - start) * height

      swipeAnimatingRef.current = true
      if (!opts?.resume) lockNavigation(NAV_LOCK_MS)
      setSwipeVisual(true)
      setSwipeAnchor(start)
      setSwipeTransition(true)
      setSwipeShift(targetShift)

      if (swipeAnimTimerRef.current) window.clearTimeout(swipeAnimTimerRef.current)
      swipeAnimTimerRef.current = window.setTimeout(() => {
        reprimeUnlockedAudio()
        finishSwipeSession(clamped)
      }, Math.abs(targetShift) < 1 ? 160 : SWIPE_ANIM_MS)
    },
    [
      finishSwipeSession,
      items.length,
      lockNavigation,
      reprimeUnlockedAudio,
      slideHeight,
    ],
  )

  const goByStep = useCallback(
    (delta: -1 | 1) => {
      if (
        swipeAnimatingRef.current ||
        navLockRef.current ||
        wheelLockRef.current ||
        swipeDragRef.current?.capturing
      ) {
        return
      }
      const start = activeIndexRef.current
      const next = start + delta
      if (next < 0 || next >= items.length) return

      // Lock synchronously — trackpads fire many wheel events before rAF.
      swipeAnimatingRef.current = true
      lockNavigation(NAV_LOCK_MS)
      gestureStartIndexRef.current = start

      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (height <= 0) {
        activeIndexRef.current = next
        setActiveIndex(next)
        setSwipeAnchor(next)
        swipeAnimatingRef.current = false
        return
      }
      setSwipeVisual(true)
      setSwipeAnchor(start)
      setSwipeTransition(false)
      setSwipeShift(0)
      requestAnimationFrame(() => {
        settleSwipeToIndex(next, { resume: true })
      })
    },
    [items.length, lockNavigation, settleSwipeToIndex, slideHeight],
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => {
      const h = el.clientHeight
      if (h > 0) setSlideHeight(h)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [feedReady])

  useLayoutEffect(() => {
    if (!items.length || didBootRef.current) return
    let target = activeIndexRef.current
    if (!startKey && !browseInitialized) {
      // Put the chosen short at index 0 so the host pool mounts once on the
      // correct active video (no index-0 play → random jump remount).
      target = chooseBrowseIndex(items)
      if (target > 0) {
        setItems((prev) => {
          if (!prev.length || target <= 0 || target >= prev.length) return prev
          const next = [...prev]
          const [chosen] = next.splice(target, 1)
          return [chosen, ...next]
        })
      }
      target = 0
      activeIndexRef.current = 0
      gestureStartIndexRef.current = 0
      setActiveIndex(0)
      setSwipeAnchor(0)
      setBrowseInitialized(true)
    } else if (startKey) {
      const idx = items.findIndex((item) => getShortFeedKey(item) === startKey)
      if (idx >= 0) {
        target = idx
        activeIndexRef.current = idx
        gestureStartIndexRef.current = idx
        setActiveIndex(idx)
        setSwipeAnchor(idx)
      }
    }
    didBootRef.current = true
    setSharedPlaying(true)
    setSwipeShift(0)
    setSwipeVisual(false)
    setSwipeTransition(false)
  }, [browseInitialized, items, startKey])

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return
    loadingRef.current = true
    setLoadingMore(true)
    try {
      const nextPage = page + 1
      const res = await fetch(`${getApiUrl()}/news/shorts-feed?page=${nextPage}&limit=10`, {
        cache: 'no-store',
      })
      if (!res.ok) return
      const data = (await res.json()) as ShortsFeedResponse
      setItems((prev) => {
        const seen = new Set(prev.map((n) => getShortFeedKey(n)))
        const fresh = (data.news || []).filter((n) => !seen.has(getShortFeedKey(n)))
        return [...prev, ...fresh]
      })
      setPage(data.page || nextPage)
      setHasMore(Boolean(data.hasMore))
    } catch {
      // Bağlantı hatasında aynı sayfayı sonsuz döngüde tekrar isteme.
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoadingMore(false)
    }
  }, [hasMore, page])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const target = e.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return
        }
      }
      e.preventDefault()
      goByStep(e.key === 'ArrowDown' ? 1 : -1)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [goByStep])

  useEffect(() => {
    const root = containerRef.current
    if (!root) return

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return
      if (Math.abs(event.deltaY) < 8) return
      if (isShortsControlTarget(event.target)) return
      event.preventDefault()
      if (
        swipeAnimatingRef.current ||
        navLockRef.current ||
        wheelLockRef.current
      ) {
        return
      }
      goByStep(event.deltaY > 0 ? 1 : -1)
    }

    root.addEventListener('wheel', onWheel, { passive: false })
    return () => root.removeEventListener('wheel', onWheel)
  }, [goByStep])

  useEffect(() => {
    if (!hasMore || loadingMore) return
    if (activeIndex >= items.length - 4) {
      void loadMore()
    }
  }, [activeIndex, hasMore, items.length, loadMore, loadingMore])

  useEffect(() => {
    if (!startKey && !browseInitialized) return
    const item = items[activeIndex]
    if (!item) return
    const target = getShortsHrefForItem(item)
    if (typeof window === 'undefined') return

    if (urlUpdateRef.current) window.clearTimeout(urlUpdateRef.current)
    urlUpdateRef.current = window.setTimeout(() => {
      if (window.location.pathname !== target) {
        window.history.replaceState(window.history.state, '', target)
      }
      syncShortsDocumentMeta(item)
    }, 120)

    return () => {
      if (urlUpdateRef.current) window.clearTimeout(urlUpdateRef.current)
    }
  }, [activeIndex, browseInitialized, items, startKey])

  useEffect(() => {
    return () => {
      if (swipeAnimTimerRef.current) window.clearTimeout(swipeAnimTimerRef.current)
      if (navLockTimerRef.current) window.clearTimeout(navLockTimerRef.current)
    }
  }, [])

  const setAudioPrefs = useCallback((next: { muted?: boolean; volume?: number }) => {
    setAudioUnlocked(true)
    const muted = next.muted ?? false
    const volume = next.volume ?? audio.volume
    // Unmute the shared host inside this user gesture.
    sharedAudioControlRef.current?.applyUserAudio(muted, volume)
    primeAllShortsAudio(muted, volume)
    setAudio((prev) => ({
      muted: next.muted ?? prev.muted,
      volume: next.volume ?? prev.volume,
    }))
  }, [audio.volume])

  const finishSwipeDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, cancelled = false) => {
      const drag = swipeDragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return

      // Clear first so pointerup + lostpointercapture cannot settle twice.
      swipeDragRef.current = null
      const root = event.currentTarget
      root.classList.remove(styles.dragging)
      try {
        if (root.hasPointerCapture(event.pointerId)) {
          root.releasePointerCapture(event.pointerId)
        }
      } catch {
        /* ignore */
      }

      if (!drag.capturing) return
      if (swipeAnimatingRef.current || navLockRef.current) return

      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (cancelled || height <= 0 || !items.length) {
        settleSwipeToIndex(drag.startIndex)
        return
      }

      if (!drag.moved) {
        finishSwipeSession(drag.startIndex)
        return
      }

      const deltaX = event.clientX - drag.startX
      const deltaY = event.clientY - drag.startY
      const vertical = Math.abs(deltaY) > Math.abs(deltaX) * 1.05
      if (!vertical) {
        settleSwipeToIndex(drag.startIndex)
        return
      }

      event.preventDefault()
      suppressDragClickRef.current = true
      window.setTimeout(() => {
        suppressDragClickRef.current = false
      }, 280)

      // Finger up → next short (same as mobile).
      const shift = clampSwipeShift(drag.startIndex, drag.startY - event.clientY, height)
      const next = resolveSettleIndex(
        drag.startIndex,
        shift,
        height,
        items.length,
        drag.velocityY,
      )
      settleSwipeToIndex(next)
    },
    [
      clampSwipeShift,
      finishSwipeSession,
      items.length,
      settleSwipeToIndex,
      slideHeight,
    ],
  )

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Touch has a dedicated path below. Handling the synthetic pointer and
      // touch streams together can settle the same swipe twice on mobile.
      if (
        event.pointerType === 'touch' ||
        event.button !== 0 ||
        isShortsControlTarget(event.target)
      ) {
        return
      }
      if (
        swipeAnimatingRef.current ||
        navLockRef.current ||
        wheelLockRef.current
      ) {
        return
      }

      const startIndex = activeIndexRef.current
      const now = performance.now()
      swipeDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startIndex,
        moved: false,
        capturing: false,
        lastY: event.clientY,
        lastAt: now,
        velocityY: 0,
      }
      gestureStartIndexRef.current = startIndex
    },
    [],
  )

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = swipeDragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return

      const deltaX = event.clientX - drag.startX
      const deltaY = event.clientY - drag.startY
      if (!drag.capturing) {
        if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
        if (Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
          swipeDragRef.current = null
          return
        }
        drag.capturing = true
        const root = containerRef.current
        root?.classList.add(styles.dragging)
        setSwipeVisual(true)
        setSwipeAnchor(drag.startIndex)
        setSwipeTransition(false)
        setSwipeShift(0)
        try {
          event.currentTarget.setPointerCapture(event.pointerId)
        } catch {
          /* older browsers */
        }
      }

      const now = performance.now()
      const dt = Math.max(1, now - drag.lastAt)
      // Finger up → positive settle velocity (same sign as shift).
      const instant = (drag.lastY - event.clientY) / dt
      drag.velocityY = drag.velocityY * 0.65 + instant * 0.35
      drag.lastY = event.clientY
      drag.lastAt = now
      drag.moved = Math.abs(deltaY) > 4 || drag.moved
      event.preventDefault()
      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (height <= 0) return
      const shift = clampSwipeShift(
        drag.startIndex,
        drag.startY - event.clientY,
        height,
      )
      setSwipeTransition(false)
      setSwipeShift(shift)
    },
    [clampSwipeShift, slideHeight],
  )

  const findTouch = useCallback(
    (touches: ReactTouchEvent<HTMLDivElement>['touches'], identifier: number) => {
      for (let index = 0; index < touches.length; index += 1) {
        const touch = touches.item(index)
        if (touch?.identifier === identifier) return touch
      }
      return null
    },
    [],
  )

  const handleTouchStart = useCallback(
    (event: ReactTouchEvent<HTMLDivElement>) => {
      if (
        event.touches.length !== 1 ||
        isShortsControlTarget(event.target) ||
        swipeAnimatingRef.current ||
        navLockRef.current ||
        wheelLockRef.current
      ) {
        return
      }

      const touch = event.touches.item(0)
      if (!touch) return
      const startIndex = activeIndexRef.current
      const now = performance.now()
      touchSwipeRef.current = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        startIndex,
        moved: false,
        capturing: false,
        lastY: touch.clientY,
        lastAt: now,
        velocityY: 0,
      }
      gestureStartIndexRef.current = startIndex
    },
    [],
  )

  const handleTouchMove = useCallback(
    (event: ReactTouchEvent<HTMLDivElement>) => {
      const drag = touchSwipeRef.current
      if (!drag) return
      const touch = findTouch(event.touches, drag.identifier)
      if (!touch) return

      const deltaX = touch.clientX - drag.startX
      const deltaY = touch.clientY - drag.startY
      if (!drag.capturing) {
        if (Math.abs(deltaY) < 6 && Math.abs(deltaX) < 6) return
        if (Math.abs(deltaX) > Math.abs(deltaY) * 1.35) {
          touchSwipeRef.current = null
          return
        }
        drag.capturing = true
        containerRef.current?.classList.add(styles.dragging)
        setSwipeVisual(true)
        setSwipeAnchor(drag.startIndex)
        setSwipeTransition(false)
        setSwipeShift(0)
      }

      const now = performance.now()
      const dt = Math.max(1, now - drag.lastAt)
      const instant = (drag.lastY - touch.clientY) / dt
      drag.velocityY = drag.velocityY * 0.65 + instant * 0.35
      drag.lastY = touch.clientY
      drag.lastAt = now
      drag.moved = Math.abs(deltaY) > 4 || drag.moved
      event.preventDefault()

      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (height <= 0) return
      setSwipeTransition(false)
      setSwipeShift(
        clampSwipeShift(
          drag.startIndex,
          drag.startY - touch.clientY,
          height,
        ),
      )
    },
    [clampSwipeShift, findTouch, slideHeight],
  )

  const finishTouchSwipe = useCallback(
    (event: ReactTouchEvent<HTMLDivElement>, cancelled = false) => {
      const drag = touchSwipeRef.current
      if (!drag) return
      const touch =
        findTouch(event.changedTouches, drag.identifier) ??
        findTouch(event.touches, drag.identifier)
      touchSwipeRef.current = null
      containerRef.current?.classList.remove(styles.dragging)

      if (!drag.capturing) return
      if (swipeAnimatingRef.current || navLockRef.current) return

      const height = slideHeight || containerRef.current?.clientHeight || 0
      if (cancelled || !touch || height <= 0 || !items.length) {
        settleSwipeToIndex(drag.startIndex)
        return
      }

      event.preventDefault()
      suppressDragClickRef.current = true
      window.setTimeout(() => {
        suppressDragClickRef.current = false
      }, 280)

      const deltaX = touch.clientX - drag.startX
      const deltaY = touch.clientY - drag.startY
      if (!drag.moved || Math.abs(deltaY) <= Math.abs(deltaX) * 1.05) {
        settleSwipeToIndex(drag.startIndex)
        return
      }

      const shift = clampSwipeShift(
        drag.startIndex,
        drag.startY - touch.clientY,
        height,
      )
      const next = resolveSettleIndex(
        drag.startIndex,
        shift,
        height,
        items.length,
        drag.velocityY,
      )
      settleSwipeToIndex(next)
    },
    [
      clampSwipeShift,
      findTouch,
      items.length,
      settleSwipeToIndex,
      slideHeight,
    ],
  )

  return (
    <div className="shorts-feed relative h-[100dvh] w-full overflow-hidden bg-[#050505] text-white">
      <header className="pointer-events-none absolute inset-x-0 top-0 z-30 flex items-center justify-between px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <Link
          href="/"
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/45 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-black/60"
        >
          <ArrowLeft className="h-4 w-4" />
          Ana Sayfa
        </Link>
        <span className="rounded-full border border-white/10 bg-black/45 px-3 py-1.5 text-sm font-bold tracking-wide text-white backdrop-blur-md">
          Shorts
        </span>
      </header>

      {youtubeReadyWindow.map(({ id, index }) => {
        const hostActive =
          feedReady && youtubeHostActive && index === activeIndex
        const distanceFromAnchor = index - layoutAnchor
        const warmAhead = shouldWarmShort(index, activeIndex)
        const swipePeer = Math.abs(distanceFromAnchor) <= 1
        // Keep +1/+2 decoding so swipe never paints a cold poster.
        const hostVisible =
          hostActive ||
          warmAhead ||
          (swipeVisual && swipePeer) ||
          index === hostWindowIndex + 1
        const hostOffset = distanceFromAnchor * slideHeight - swipeShift
        // Incoming/outgoing hosts are on-screen during the swipe — never rewind them.
        const suppressWarmRewind =
          hostActive || Math.abs(hostOffset) < Math.max(slideHeight, 1) * 0.92
        return (
          <div
            // Stable key — live/idle remounts caused a post-swipe flash.
            key={`yt-${id}-${index}`}
            className={`${styles.sharedYoutubeLayer}${
              hostVisible ? '' : ` ${styles.sharedYoutubeLayerPreload}`
            }`}
            style={{
              transform: `translate3d(0, ${hostOffset}px, 0)`,
              transition: swipeTransition
                ? `transform ${SWIPE_ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                : 'none',
              // Keep hosts under the chrome layer (scrollRoot z-index: 2).
              zIndex: hostActive ? 1 : 0,
              opacity: hostVisible ? 1 : undefined,
            }}
            aria-hidden={!hostActive}
          >
            <div className={styles.sharedYoutubeFrame}>
              <ShortYoutubePlayer
                youtubeId={id}
                shared
                isActive={hostActive}
                shouldPreload={!hostActive}
                shouldWarm={!hostActive && warmAhead}
                warmDelayMs={warmDelayForIndex(index, activeIndex)}
                suppressWarmRewind={suppressWarmRewind}
                playing={sharedPlaying && hostActive}
                muted={audio.muted}
                volume={audio.volume}
                audioUnlocked={audioUnlocked}
                audioControlRef={
                  hostActive ? sharedAudioControlRef : undefined
                }
                onPlayingChange={hostActive ? setSharedPlaying : undefined}
                onTimeUpdate={hostActive ? updateFeedProgress : undefined}
              />
            </div>
          </div>
        )
      })}

      <div
        ref={containerRef}
        className={styles.scrollRoot}
        onClickCapture={(event) => {
          if (!suppressDragClickRef.current) return
          event.preventDefault()
          event.stopPropagation()
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={(event) => finishSwipeDrag(event)}
        onPointerCancel={(event) => finishSwipeDrag(event, true)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={(event) => finishTouchSwipe(event)}
        onTouchCancel={(event) => finishTouchSwipe(event, true)}
        onLostPointerCapture={(event) => {
          if (!swipeDragRef.current) return
          finishSwipeDrag(event, true)
        }}
      >
        <div
          className={styles.slideTrack}
          style={{
            transform: `translate3d(0, ${trackOffset}px, 0)`,
            transition: swipeTransition
              ? `transform ${SWIPE_ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
              : 'none',
          }}
        >
          {items.map((item, index) => (
            <ShortVideoSlide
              key={getShortFeedKey(item)}
              item={item}
              isActive={feedReady && index === activeIndex}
              shouldPreload={feedReady && shouldPreloadShort(index, activeIndex)}
              shouldWarm={feedReady && shouldWarmShort(index, activeIndex)}
              muted={audio.muted}
              volume={audio.volume}
              audioUnlocked={audioUnlocked}
              useSharedYoutube
              sharedPlaying={sharedPlaying}
              onSharedPlayingChange={setSharedPlaying}
              onAudioChange={setAudioPrefs}
            />
          ))}
        </div>

        {!items.length ? (
          <div className="flex h-[100dvh] min-h-[100dvh] flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-lg font-medium text-white">Henüz shorts içeriği yok</p>
            <Link href="/" className="text-sm text-red-400 hover:underline">
              Ana sayfaya dön
            </Link>
          </div>
        ) : null}
      </div>

      {feedReady && youtubeHostActive ? (
        <div className={styles.feedYoutubeProgress} data-shorts-control>
          <span
            ref={feedRemainingRef}
            className={styles.feedProgressRemaining}
            aria-live="polite"
          >
            0:00
          </span>
          <input
            ref={feedProgressRef}
            type="range"
            min="0"
            max="100"
            step="0.05"
            defaultValue="0"
            aria-label="Video ilerleme"
            onPointerDown={(event) => {
              event.stopPropagation()
              feedSeekingRef.current = true
            }}
            onPointerUp={(event) => {
              event.stopPropagation()
              feedSeekingRef.current = false
              const time = sharedAudioControlRef.current?.getPlaybackTime()
              if (time) updateFeedProgress(time.current, time.duration)
            }}
            onPointerCancel={(event) => {
              event.stopPropagation()
              feedSeekingRef.current = false
              const time = sharedAudioControlRef.current?.getPlaybackTime()
              if (time) updateFeedProgress(time.current, time.duration)
            }}
            onInput={(event) => {
              const target = event.currentTarget
              const duration = Math.max(0, Number(target.max) || 0)
              const nextTime = Math.min(
                duration || Number.MAX_SAFE_INTEGER,
                Math.max(0, Number(target.value) || 0),
              )
              seekActiveShort(nextTime)
              const progress =
                duration > 0 ? Math.min(100, (nextTime / duration) * 100) : 0
              target.style.setProperty('--shorts-progress', `${progress}%`)
              if (feedRemainingRef.current) {
                feedRemainingRef.current.textContent = formatRemaining(
                  nextTime,
                  duration,
                )
              }
            }}
          />
        </div>
      ) : null}
    </div>
  )
}
